import { apiRequest, useAccount, VojomoLoader } from "@vojomo-core/shared";
import { Button, Modal, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DevPanel } from "../../components/DevPanel/DevPanel";
import { BusinessSignupForm } from "../BusinessSignup/BusinessSignup";

const IS_DEV = import.meta.env.VITE_REACT_APP_ENV === "dev";

interface Member {
  id: string;
  name: string;
  weekly_agenda: boolean;
}

export function Account(): React.ReactElement {
  const {
    vojomoAccount,
    companyName,
    updateImpersonatedCompany,
    isUpdatingCompany
  } = useAccount();

  const [isLoading, setIsLoading] = useState(false);
  const [showCreateCompany, setShowCreateCompany] = useState(false);

  const isCompanyAdmin =
    (vojomoAccount.company_admin_companies?.length ?? 0) > 0;

  const roleContent =
    vojomoAccount.is_admin || vojomoAccount.is_super_admin || isCompanyAdmin ? (
      <div>
        <strong>Roles</strong>:
        <ul className="m-0">
          {vojomoAccount.is_super_admin && <li>Super Admin</li>}
          {vojomoAccount.is_admin && <li>Admin</li>}
          {isCompanyAdmin && (
            <li>
              Company Admin (
              {vojomoAccount.company_admin_companies!.join(", ")})
            </li>
          )}
        </ul>
      </div>
    ) : null;

  const [members, setMembers] = useState<Member[]>([]);

  const filteredMembers = useMemo(() => {
    return members
      .filter((member) => member.name !== companyName)
      .map((member) => {
        return { value: member.name };
      });
  }, [members, companyName]);

  const callApiMembers = async () => {
    setIsLoading(true);
    const data = await apiRequest<Member[]>({
      subRoute: "utils",
      path: "/members"
    });

    if (data) {
      setMembers(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (vojomoAccount.is_admin) {
      callApiMembers();
    }
  }, [vojomoAccount.is_admin]);

  return (
    <div>
      <h3>My Account</h3>
      {isLoading ? (
        <VojomoLoader />
      ) : (
        <>
          <h4>{vojomoAccount.name}</h4>
          <strong>ID</strong>: {vojomoAccount.id}
          <br />
          <strong>Email</strong>: {vojomoAccount.email}
          <br />
          <strong>Company</strong>: {companyName}
          <br />
          <br />
          <Button onClick={() => setShowCreateCompany(true)}>
            Create New Company
          </Button>
          <Modal
            title="Create New Company"
            open={showCreateCompany}
            onCancel={() => setShowCreateCompany(false)}
            footer={null}
            destroyOnClose
          >
            <div className="pt-2">
              <BusinessSignupForm
                autoFocus={false}
                onSuccess={(newCompanyName) => {
                  updateImpersonatedCompany(newCompanyName);
                  setShowCreateCompany(false);
                }}
              />
            </div>
          </Modal>
          <br />
          {roleContent}
          {vojomoAccount.is_admin && (
            <>
              <br />
              <h5 className="mb-3 font-bold">Change member company</h5>
              <Select
                className="mb-4 w-80"
                id="doc-type"
                options={filteredMembers}
                placeholder="Select a different company here..."
                showSearch
                loading={isUpdatingCompany}
                onChange={async (value) => {
                  if (value !== companyName) {
                    await updateImpersonatedCompany(value);
                  }
                }}
              />
            </>
          )}
          {(vojomoAccount.is_admin || isCompanyAdmin) && (
            <>
              <br />
              <h5 className="mb-3 font-bold">
                Manage{" "}
                {(vojomoAccount.company_admin_companies?.length ?? 0) > 1
                  ? "companies"
                  : "company"}
              </h5>
              <Link style={{ color: "#2c1561" }} to="/company_admin/users">
                Users
              </Link>
            </>
          )}
          {IS_DEV && (
            <>
              <br />
              <br />
              <h5 className="mb-2 font-bold">Dev Tools</h5>
              <DevPanel />
            </>
          )}
        </>
      )}
    </div>
  );
}
